import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Wifi, User, Mail, Phone, Building, MapPin, Calendar,
  Download, Upload, DollarSign, Clock, LogOut, RefreshCw,
  AlertCircle, CheckCircle, Globe, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const ISPCustomerDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const customerId = localStorage.getItem('customer_id');
    if (!customerId) {
      navigate('/customer/login');
      return;
    }
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const customerId = localStorage.getItem('customer_id');
      const response = await fetch(`/api/customer/dashboard?customer_id=${customerId}`);
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        toast.error('Error loading dashboard');
      }
      setLoading(false);
    } catch (error) {
      toast.error('Error loading dashboard');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_id');
    localStorage.removeItem('company_name');
    navigate('/customer/login');
    toast.success('Logged out successfully');
  };

  const handleRenewal = async () => {
    try {
      const { plan } = dashboardData;
      
      if (!plan) {
        toast.error('No active plan found');
        return;
      }

      // Show payment instructions dialog
      const paymentInstructions = `
PLAN RENEWAL PAYMENT

Plan: ${plan.plan_name}
Amount: ₹${plan.plan_cost.toLocaleString()}

PAYMENT OPTIONS:

1. UPI Payment:
   • Open any UPI app (Google Pay, PhonePe, Paytm)
   • Scan QR code or use UPI ID
   • Amount: ₹${plan.plan_cost}
   • Description: Plan Renewal - ${customer.customer_id}

2. Bank Transfer:
   • Account Name: Tech360 Technologies
   • Amount: ₹${plan.plan_cost}
   • Reference: ${customer.customer_id}

3. Razorpay Link:
   Visit: razorpay.me/@tech360technologies
   Enter Amount: ${plan.plan_cost}

After payment:
• WhatsApp payment screenshot to: +91-6307506664
• Email: stark360t@gmail.com
• Include Customer ID: ${customer.customer_id}

Your plan will be activated within 1 hour of payment verification.

Click OK to copy payment details.
      `.trim();

      if (window.confirm(paymentInstructions)) {
        // Copy customer ID to clipboard
        navigator.clipboard.writeText(customer.customer_id).then(() => {
          toast.success('Customer ID copied! Send this with payment proof.', { duration: 5000 });
        });

        // Try to open Razorpay link
        const paymentUrl = `https://razorpay.me/@tech360technologies`;
        window.open(paymentUrl, '_blank');
        
        // Show follow-up instructions
        setTimeout(() => {
          toast.info(
            'After payment, WhatsApp screenshot with Customer ID to +91-6307506664',
            { duration: 10000 }
          );
        }, 2000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      // Fallback: Show contact details
      toast.error(
        `Payment link not available. Please contact:\nWhatsApp: +91-6307506664\nEmail: stark360t@gmail.com\nCustomer ID: ${customer?.customer_id}`,
        { duration: 10000 }
      );
    }
  };

  const getExpiryStatus = (daysRemaining) => {
    if (daysRemaining === null || daysRemaining === undefined) return 'gray';
    if (daysRemaining < 0) return 'expired';
    if (daysRemaining <= 7) return 'critical';
    if (daysRemaining <= 15) return 'warning';
    return 'good';
  };

  const getExpiryMessage = (daysRemaining) => {
    if (daysRemaining === null || daysRemaining === undefined) return 'No plan active';
    if (daysRemaining < 0) return `Expired ${Math.abs(daysRemaining)} Days Ago`;
    if (daysRemaining === 0) return 'Expires Today';
    if (daysRemaining === 1) return 'Expires Tomorrow';
    return `Expires in ${daysRemaining} Days`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'good': 'bg-gradient-to-br from-green-400 to-green-600',
      'warning': 'bg-gradient-to-br from-orange-400 to-orange-600',
      'critical': 'bg-gradient-to-br from-red-400 to-red-600',
      'expired': 'bg-gradient-to-br from-gray-700 to-gray-900 animate-pulse',
      'gray': 'bg-gradient-to-br from-gray-400 to-gray-600'
    };
    return colors[status] || colors.gray;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { customer, plan, days_remaining } = dashboardData || {};
  const expiryStatus = getExpiryStatus(days_remaining);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-6 px-4 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Wifi className="w-8 h-8" />
              Customer Portal
            </h1>
            <p className="text-blue-200 mt-1">{customer?.company_name}</p>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={fetchDashboard}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Expiry Status Card - Large Colored Card */}
        {plan && (
          <Card className={`mb-8 ${getStatusColor(expiryStatus)} text-white shadow-2xl border-0`}>
            <CardContent className="py-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Clock className="w-16 h-16" />
                </div>
                <h2 className="text-3xl font-bold mb-2">{plan.plan_name}</h2>
                <p className="text-5xl font-extrabold mb-4">{getExpiryMessage(days_remaining)}</p>
                <div className="text-xl opacity-90">
                  Expiry Date: {new Date(plan.expiry_date).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </div>
                {days_remaining !== null && days_remaining <= 30 && (
                  <Button
                    onClick={() => handleRenewal()}
                    className="mt-6 bg-white text-blue-900 hover:bg-blue-50 font-bold text-lg px-8 py-6"
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    Renew Now - Pay ₹{plan.plan_cost.toLocaleString()}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Account Information */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Company Name</p>
                    <p className="font-semibold text-gray-900">{customer?.company_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Contact Person</p>
                    <p className="font-semibold text-gray-900">{customer?.contact_person}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-semibold text-gray-900">{customer?.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Mobile</p>
                    <p className="font-semibold text-gray-900">{customer?.mobile}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Customer ID</p>
                    <p className="font-semibold text-gray-900 font-mono">{customer?.customer_id}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-1 ${customer?.status === 'active' ? 'text-green-600' : 'text-red-600'}`} />
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <Badge className={customer?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {customer?.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Internet Plan Details */}
          {plan && (
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Wifi className="w-5 h-5" />
                  Internet Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Plan Name</p>
                      <p className="font-semibold text-gray-900">{plan.plan_name}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-green-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Download Speed</p>
                      <p className="font-semibold text-gray-900">{plan.download_speed}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Upload className="w-5 h-5 text-orange-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Upload Speed</p>
                      <p className="font-semibold text-gray-900">{plan.upload_speed}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Plan Cost</p>
                      <p className="font-semibold text-gray-900 text-2xl">₹{plan.plan_cost.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Activation Date</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(plan.activation_date).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">Expiry Date</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(plan.expiry_date).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Additional Info */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Installation & Technical Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Installation Address</p>
                <p className="font-semibold text-gray-900">{customer?.installation_address}</p>
              </div>

              {customer?.gst_number && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">GST Number</p>
                  <p className="font-semibold text-gray-900 font-mono">{customer.gst_number}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-1">Connection Type</p>
                <Badge className="bg-blue-100 text-blue-800">{customer?.connection_type}</Badge>
              </div>

              {customer?.static_ip && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Static IP Address</p>
                  <p className="font-semibold text-gray-900 font-mono">{customer.static_ip}</p>
                </div>
              )}

              {customer?.account_manager && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Account Manager</p>
                  <p className="font-semibold text-gray-900">{customer.account_manager}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ISPCustomerDashboard;
